import React, { Fragment, useContext, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { ReactComponent as MetricalLogoWhite } from 'assets/metrical-logo-white.svg';
import { HEADER } from 'config';

import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { alpha, useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { SessionContext } from '../../../auth';

import { GLOBAL_NAV_ITEMS } from './model';
import type { NavItem, NavVariant } from './types';

export const NAV_WIDTH = 180;
export const NAV_RAIL_WIDTH = 48;

function iconForItem(id: string) {
  if (id.startsWith('dashboard')) return <DashboardOutlinedIcon fontSize="small" />;
  if (id.startsWith('exec')) return <AnalyticsOutlinedIcon fontSize="small" />;
  if (id.includes('roles')) return <GroupsOutlinedIcon fontSize="small" />;
  if (id.includes('orgs') || id.includes('organizations')) return <ApartmentOutlinedIcon fontSize="small" />;
  if (id.includes('org')) return <Inventory2OutlinedIcon fontSize="small" />;
  if (id.startsWith('accounts')) return <SettingsOutlinedIcon fontSize="small" />;
  if (id.startsWith('customer')) return <BusinessOutlinedIcon fontSize="small" />;
  if (id.startsWith('profile')) return <AccountCircleOutlinedIcon fontSize="small" />;
  return <DashboardOutlinedIcon fontSize="small" />;
}

function flattenToLeafRoutes(items: NavItem[]): Array<{ id: string; label: string; href: string }> {
  const out: Array<{ id: string; label: string; href: string }> = [];
  items.forEach((item) => {
    if (item.children?.length) {
      item.children.forEach((child) => {
        if (child.href) out.push({ id: child.id, label: `${item.label} / ${child.label}`, href: child.href });
      });
      return;
    }
    if (item.href) out.push({ id: item.id, label: item.label, href: item.href });
  });
  return out;
}

function getActiveHref(locationPathname: string, leafRoutes: Array<{ href: string }>) {
  const sorted = [...leafRoutes].sort((a, b) => b.href.length - a.href.length);
  return sorted.find(({ href }) => locationPathname.startsWith(href))?.href ?? false;
}

export type GlobalNavProps = {
  variant: NavVariant;
  headerOffsetPx?: number;
  disableFixed?: boolean;
  drawerOpen?: boolean;
  onDrawerOpenChange?: (open: boolean) => void;
};

export default function GlobalNav({
  variant,
  headerOffsetPx,
  disableFixed,
  drawerOpen,
  onDrawerOpenChange,
}: GlobalNavProps) {
  const theme = useTheme();
  const location = useLocation();
  const { profile } = useContext(SessionContext);
  const headerOffset = headerOffsetPx ?? HEADER.MAIN_DESKTOP_HEIGHT;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = drawerOpen ?? internalOpen;
  const setOpen = (next: boolean) => {
    setInternalOpen(next);
    onDrawerOpenChange?.(next);
  };

  const visibleItems = useMemo<NavItem[]>(() => {
    if (profile?.admin) return GLOBAL_NAV_ITEMS;
    return GLOBAL_NAV_ITEMS.filter((x) => x.id !== 'accounts');
  }, [profile?.admin]);

  const leafRoutes = useMemo(() => flattenToLeafRoutes(visibleItems), [visibleItems]);
  const activeHref = useMemo(() => getActiveHref(location.pathname, leafRoutes), [leafRoutes, location.pathname]);
  const isExec = location.pathname.startsWith('/exec');
  const execToggle = useMemo(() => ({
    label: isExec ? 'Detailed View' : 'Exec View',
    href: isExec ? '/dashboard' : '/exec',
  }), [isExec]);

  if (variant === 'off') return null;

  const showHybridHeader = variant === 'hybrid';
  const railWidth = NAV_RAIL_WIDTH;
  const remainingItems = useMemo(() => visibleItems, [visibleItems]);

  return (
    <>
      {showHybridHeader && null}
      <Drawer
        variant="permanent"
        open
        sx={{
          width: railWidth,
          flexShrink: 0,
          display: { xs: 'none', lg: 'block' },
          '& .MuiDrawer-paper': {
            width: railWidth,
            boxSizing: 'border-box',
            borderRight: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            top: disableFixed ? 0 : headerOffset,
            height: disableFixed ? '100%' : `calc(100% - ${headerOffset}px)`,
            // Overlay rail; do not reflow page content
            position: 'fixed',
            left: 0,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: 1, py: 1 }}>
          <Tooltip title="Open navigation">
            <IconButton size="small" onClick={() => setOpen(true)} aria-label="Open navigation">
              <MenuOpenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Divider />
        <List sx={{ py: 1 }}>
          <Tooltip title={execToggle.label} placement="right">
            <ListItemButton
              component={RouterLink}
              to={execToggle.href}
              sx={{
                borderRadius: 1,
                mx: 0.5,
                my: 0.25,
                px: 1,
                justifyContent: 'center',
              }}
            >
              <ListItemIcon sx={{ minWidth: 'auto' }}>
                <SwapHorizIcon fontSize="small" />
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>

          {remainingItems.map((item) => (
            <Fragment key={item.id}>
              {item.children?.length ? (
                <Box sx={{ px: 0.5, pb: 0.5 }}>
                  {item.children.map((child) => {
                    const selected = Boolean(child.href && activeHref && activeHref === child.href);
                    return (
                      <Tooltip key={child.id} title={child.label} placement="right">
                        <ListItemButton
                          component={RouterLink}
                          to={child.href ?? '#'}
                          selected={selected}
                          sx={{
                            borderRadius: 1,
                            mx: 0.5,
                            my: 0.25,
                            px: 1,
                            justifyContent: 'center',
                            '&.Mui-selected': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.08),
                              '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.12) },
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 'auto', color: selected ? theme.palette.primary.main : theme.palette.text.secondary }}>
                            {iconForItem(child.id)}
                          </ListItemIcon>
                        </ListItemButton>
                      </Tooltip>
                    );
                  })}
                </Box>
              ) : (
                <Tooltip title={item.label} placement="right">
                  <ListItemButton
                    component={RouterLink}
                    to={item.href ?? '#'}
                    selected={Boolean(item.href && activeHref && activeHref === item.href)}
                    sx={{
                      borderRadius: 1,
                      mx: 0.5,
                      my: 0.25,
                      px: 1,
                      justifyContent: 'center',
                      '&.Mui-selected': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.12) },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 'auto' }}>
                      {iconForItem(item.id)}
                    </ListItemIcon>
                  </ListItemButton>
                </Tooltip>
              )}
            </Fragment>
          ))}
        </List>
      </Drawer>

      {/* Full nav as overlay drawer */}
      <Drawer
        anchor="left"
        open={open}
        onClose={() => setOpen(false)}
        variant="temporary"
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', lg: 'block' },
          '& .MuiDrawer-paper': {
            width: NAV_WIDTH,
            boxSizing: 'border-box',
            borderRight: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            top: 0,
            height: '100%',
            position: 'fixed',
          },
        }}
      >
        <Box
          sx={{
            height: HEADER.MAIN_DESKTOP_HEIGHT,
            backgroundColor: '#4559A7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1,
          }}
        >
          <Box
            component={RouterLink}
            to="/dashboard"
            onClick={() => setOpen(false)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
              textDecoration: 'none',
            }}
          >
            <Box
              component={MetricalLogoWhite}
              sx={{ width: 120, height: 24, flexShrink: 0 }}
              aria-label="Metrical"
            />
          </Box>
          <Tooltip title="Close">
            <IconButton
              size="small"
              onClick={() => setOpen(false)}
              aria-label="Close navigation"
              sx={{ p: 0, ml: 7.5, mt: 0.5 }}
            >
              <MenuOpenIcon
                fontSize="inherit"
                sx={{
                  color: 'white',
                  fontSize: 18,
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
        <Divider />
        <List sx={{ py: 1 }}>
          <ListItemButton
            component={RouterLink}
            to={execToggle.href}
            onClick={() => setOpen(false)}
            sx={{
              borderRadius: 1,
              mx: 1,
              my: 0.25,
              px: 1.25,
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <SwapHorizIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={execToggle.label} primaryTypographyProps={{ fontSize: 13 }} />
          </ListItemButton>
          <Divider sx={{ my: 1 }} />
          {remainingItems.map((item) => (
            <Fragment key={item.id}>
              {item.children?.length ? (
                <Box sx={{ px: 1, pb: 0.5 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: theme.palette.text.secondary, px: 1, py: 0.75 }}>
                    {item.label}
                  </Typography>
                  {item.children.map((child) => {
                    const selected = Boolean(child.href && activeHref && activeHref === child.href);
                    return (
                      <ListItemButton
                        key={child.id}
                        component={RouterLink}
                        to={child.href ?? '#'}
                        selected={selected}
                        onClick={() => setOpen(false)}
                        sx={{
                          borderRadius: 1,
                          my: 0.25,
                          px: 1.25,
                          '&.Mui-selected': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.08),
                            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.12) },
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, color: selected ? theme.palette.primary.main : theme.palette.text.secondary }}>
                          {iconForItem(child.id)}
                        </ListItemIcon>
                        <ListItemText primary={child.label} primaryTypographyProps={{ fontSize: 13 }} />
                      </ListItemButton>
                    );
                  })}
                </Box>
              ) : (
                <ListItemButton
                  component={RouterLink}
                  to={item.href ?? '#'}
                  selected={Boolean(item.href && activeHref && activeHref === item.href)}
                  onClick={() => setOpen(false)}
                  sx={{
                    borderRadius: 1,
                    mx: 1,
                    my: 0.25,
                    px: 1.25,
                    '&.Mui-selected': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.12) },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {iconForItem(item.id)}
                  </ListItemIcon>
                  <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 13 }} />
                </ListItemButton>
              )}
            </Fragment>
          ))}
        </List>
      </Drawer>
    </>
  );
}

